/**
 * SELECTOR DE PAÍS — HIDRATACIÓN DIFERIDA
 * ---------------------------------------------------------------------------------------------------------------------
 * El selector de país del snippet 'localization-selector' recorre "localization.available_countries". Con los mercados
 * abiertos a todo el mundo eso son 237 opciones, y el selector se pinta tres veces por página (cabecera de escritorio,
 * barra lateral móvil y pie), así que el documento arrastraba ~1.665 nodos y 237 <img> de banderas en TODAS las
 * páginas: el 37-45% del DOM y el 16-27% del HTML, para un desplegable que casi nadie abre.
 *
 * La lista sigue viniendo del servidor —hace falta para que el <form> de localización funcione sin JavaScript de por
 * medio— pero se emite dentro de un <template>. El contenido de un <template> se analiza y se queda en un
 * DocumentFragment inerte: no entra en el árbol de renderizado, no paga cálculo de estilo ni maquetación, y sus
 * imágenes no se solicitan nunca. El coste en reposo pasa a ser cero.
 *
 * Se hidrata al primer indicio de que el usuario va a abrir el desplegable. Los cinco eventos cubren las tres formas de
 * llegar a él —ratón, teclado y toque— y todos se disparan antes de que el clic termine de propagarse, de modo que
 * <x-popover> se abre con las opciones ya en su sitio.
 *
 * Detalle importante: <x-listbox> indexa sus opciones una sola vez, en connectedCallback, y se protege con una bandera
 * interna para no repetirlo. Rellenar el listbox existente dejaría esas opciones sin identificadores ni
 * aria-activedescendant, así que en su lugar se construye un <x-listbox> nuevo con las opciones ya dentro y se sustituye
 * el vacío: al conectarse, el elemento nuevo ejecuta su propia inicialización de accesibilidad con la lista completa.
 */

(() => {
  const WRAPPER_SELECTOR = '[data-localization-lazy]';
  const INTENT_EVENTS = ['pointerenter', 'focusin', 'pointerdown', 'touchstart'];

  /**
   * Al abrirse, <x-popover> se traslada a <body> para escaparse de los contextos de apilamiento de la cabecera. La
   * hidratación se dispara en "pointerdown", antes de que eso ocurra, pero si el desplegable llegara a abrirse por otra
   * vía el listbox ya no colgaría del contenedor: se localiza entonces por el aria-controls del propio botón.
   */
  const findListbox = (wrapper) => {
    const inWrapper = wrapper.querySelector('x-listbox[data-localization-listbox]');

    if (inWrapper) return inWrapper;

    const popoverId = wrapper.querySelector('[aria-controls]')?.getAttribute('aria-controls');

    return popoverId
      ? document.getElementById(popoverId)?.querySelector('x-listbox[data-localization-listbox]') ?? null
      : null;
  };

  const hydrate = (wrapper) => {
    const listbox = findListbox(wrapper);
    const template = listbox?.querySelector('template[data-localization-options]');

    if (!template) return;

    const hydrated = document.createElement('x-listbox');

    for (const { name, value } of listbox.attributes) {
      if (name !== 'data-localization-listbox') hydrated.setAttribute(name, value);
    }

    /* append mueve los nodos del fragmento; no se clona nada */
    hydrated.append(template.content);
    listbox.replaceWith(hydrated);
  };

  const observe = (wrapper) => {
    if (wrapper.dataset.localizationHydrated) return;

    wrapper.dataset.localizationHydrated = 'pending';

    const controller = new AbortController();
    const run = () => {
      controller.abort();
      wrapper.dataset.localizationHydrated = 'done';
      hydrate(wrapper);
    };

    for (const type of INTENT_EVENTS) {
      wrapper.addEventListener(type, run, { signal: controller.signal, passive: true });
    }

    /* Red de seguridad en fase de captura: corre antes que cualquier manejador de clic del tema */
    wrapper.addEventListener('click', run, { signal: controller.signal, capture: true });
  };

  const scan = (root = document) => root.querySelectorAll(WRAPPER_SELECTOR).forEach(observe);

  scan();

  /* El editor de temas vuelve a pintar secciones enteras: las copias nuevas necesitan sus propios escuchadores */
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
